import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import HomeHero from './home/HomeHero';
import HomeTopics from './home/HomeTopics';
import HomeDiscussions from './home/HomeDiscussions';
import HomePoll from './home/HomePoll';
import HomeCta from './home/HomeCta';

/**
 * BlueEmotion · custom homepage.
 *
 * This component is just the data orchestrator + layout shell: it loads the
 * forum data the page needs (latest / most-commented discussions and the
 * newest active poll) and composes the section components in ./home/, passing
 * each the slice of data it renders. All markup lives in those modules; all
 * styling lives in less/forum/HomePage.less.
 *
 * Registered as the `glowingblue-demo-home` route (see forum/extend.ts via
 * Extend.Routes, paired with the PHP-side Frontend->route() in extend.php) and
 * offered as a default-homepage option in admin (see admin extenders).
 */
export default class HomePage extends Page {
  constructor() {
    super(...arguments);
    // Declared with definite-assignment (not class-field initializers).
    // Flarum/Babel class-field initializers can desync from the instance
    // Mithril renders; initializing inside oninit (like core's TagsPage) is
    // the proven pattern.
    _defineProperty(this, "loading", void 0);
    _defineProperty(this, "latest", void 0);
    _defineProperty(this, "commented", void 0);
    _defineProperty(this, "poll", void 0);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.loading = true;
    this.latest = [];
    this.commented = [];
    this.poll = null;
    this.bodyClass = 'App--index';
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.loadData();
  }
  loadData() {
    // Explicitly include the relationships the cards render so
    // discussion.user()/lastPostedUser()/tags() are populated and the
    // card markup never has to lazy-trigger another request mid-render.
    const include = 'user,lastPostedUser,tags';
    const latest = app.store.find('discussions', {
      include,
      sort: '',
      page: {
        limit: 4
      }
    });
    const commented = app.store.find('discussions', {
      include,
      sort: '-commentCount',
      page: {
        limit: 4
      }
    });

    // fof/polls is optional. If it isn't installed the /api/polls endpoint
    // 404s — we catch and simply omit the poll section.
    // fof/polls's isEnded filter ignores its value (`isEnded=0` still means
    // ended); the negated key selects open polls, as its own showcase does.
    const poll = app.store.find('polls', {
      filter: {
        '-isEnded': '1',
        isDraft: '0'
      },
      sort: '-createdAt',
      page: {
        limit: 1
      }
    }).then(res => Array.isArray(res) ? res[0] : res).catch(() => null);
    Promise.all([latest, commented, poll]).then(_ref => {
      let _ref2 = _slicedToArray(_ref, 3),
        latest = _ref2[0],
        commented = _ref2[1],
        poll = _ref2[2];
      this.latest = latest;
      this.commented = commented;
      this.poll = poll;
      this.loading = false;
      m.redraw();
    }).catch(() => {
      this.loading = false;
      m.redraw();
    });
  }
  view() {
    return m("div", {
      className: "HomePage"
    }, m(HomeHero, {
      poll: this.poll
    }), m(HomeTopics, null), this.loading ? m("div", {
      className: "container",
      style: {
        padding: '60px 0'
      }
    }, m(LoadingIndicator, {
      size: "large"
    })) : [m(HomeDiscussions, {
      latest: this.latest,
      commented: this.commented
    }), m(HomePoll, {
      poll: this.poll
    })], m(HomeCta, null));
  }
}
flarum.reg.add('glowingblue-demo', 'forum/components/HomePage', HomePage);